// components/UserAccessContent.jsx
import React, { useState, useEffect } from 'react';
import { 
  FaUsers, 
  FaBuilding, 
  FaSearch, 
  FaEye,
  FaTrash,
  FaCheck,
  FaTimes,
  FaSync,
  FaUser,
  FaChevronDown,
  FaChevronUp,
  FaFilter,
  FaList,
  FaTimesCircle,
  FaExternalLinkAlt,
  FaExclamationTriangle,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaChevronLeft,
  FaChevronRight
} from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';
import CONFIG from '../config';

const UserAccessContent = () => {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState([]);
  const [showCompaniesModal, setShowCompaniesModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [userCompanies, setUserCompanies] = useState([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: 'email', direction: 'asc' });
  
  // State for admin companies popup
  const [showAdminCompaniesPopup, setShowAdminCompaniesPopup] = useState(false);
  const [adminCompaniesList, setAdminCompaniesList] = useState([]);
  const [adminCompaniesLoading, setAdminCompaniesLoading] = useState(false);
  
  // SIMPLIFIED STATE FOR DELETE USER
  const [userToDelete, setUserToDelete] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Get auth token using CONFIG helper
  const getToken = () => {
    return CONFIG.getToken();
  };

  // Get auth headers using CONFIG helper
  const getAuthHeaders = () => {
    return CONFIG.getAuthHeaders();
  };

  // Fetch all managed users for the current admin
  const fetchManagedUsers = async () => {
    try {
      setLoading(true);
      
      // First get current admin info
      const currentUserResponse = await axios.get(
        CONFIG.getFullUrl(CONFIG.API.USERS.ME),
        { headers: getAuthHeaders() }
      );
      
      const adminId = currentUserResponse.data.id;
      
      // Get managed users for this admin using correct endpoint
      const response = await axios.get(
        CONFIG.getFullUrl(`/users/${adminId}/managed-users`),
        { headers: getAuthHeaders() }
      );
      
      // Handle empty response
      if (!response.data || !Array.isArray(response.data)) {
        setUsers([]);
        setFilteredUsers([]);
        return;
      }
      
      // Fetch company count for each user
      const usersWithCompanyCounts = await Promise.all(
        response.data.map(async (user) => {
          try {
            const companiesResponse = await axios.get(
              CONFIG.getFullUrl(CONFIG.API.USERS.USER_COMPANIES(user.id)),
              { headers: getAuthHeaders() }
            );
            
            return {
              ...user,
              companies: companiesResponse.data || [],
              companyCount: Array.isArray(companiesResponse.data) ? companiesResponse.data.length : 0
            };
          } catch (error) {
            console.error(`Error fetching companies for user ${user.id}:`, error);
            return {
              ...user,
              companies: [],
              companyCount: 0
            };
          }
        })
      );
      
      setUsers(usersWithCompanyCounts);
      setFilteredUsers(usersWithCompanyCounts);
      
    } catch (error) {
      console.error('Error fetching managed users:', error);
      
      if (error.response) {
        if (error.response.status === 404) {
          toast.info('No managed users found. Users will appear here once assigned.');
          setUsers([]);
          setFilteredUsers([]);
        } else if (error.response.status === 401) {
          toast.error('Session expired. Please login again.');
        } else {
          toast.error(`Failed to load managed users: ${error.response.data?.detail || error.message}`);
        }
      } else {
        toast.error('Failed to load managed users. Please check your connection.');
      }
      
      setUsers([]);
      setFilteredUsers([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch companies managed by current admin
  const fetchAdminCompanies = async () => {
    try {
      const response = await axios.get(
        CONFIG.getFullUrl(CONFIG.API.COMPANIES.MY_MANAGED_COMPANIES),
        { headers: getAuthHeaders() }
      );
      
      // Handle empty response
      if (!response.data || !Array.isArray(response.data)) {
        setCompanies([]);
        return;
      }
      
      setCompanies(response.data);
      
    } catch (error) {
      console.error('Error fetching admin companies:', error);
      
      if (error.response) {
        if (error.response.status === 404) {
          toast.info('No companies assigned to you yet.');
          setCompanies([]);
        } else if (error.response.status === 401) {
          toast.error('Session expired. Please login again.');
        } else {
          toast.error(`Failed to load companies: ${error.response.data?.detail || error.message}`);
        }
      } else {
        toast.error('Failed to load companies. Please check your connection.');
      }
      
      setCompanies([]);
    }
  };

  // Fetch admin companies for popup list
  const fetchAdminCompaniesForPopup = async () => {
    try {
      setAdminCompaniesLoading(true);
      
      const response = await axios.get(
        CONFIG.getFullUrl(CONFIG.API.COMPANIES.MY_MANAGED_COMPANIES),
        { headers: getAuthHeaders() }
      );
      
      // Handle empty response
      if (!response.data || !Array.isArray(response.data)) {
        setAdminCompaniesList([]);
        return;
      }
      
      setAdminCompaniesList(response.data);
    } catch (error) {
      console.error('Error fetching admin companies for popup:', error);
      
      if (error.response) {
        if (error.response.status === 404) {
          toast.info('No companies available.');
          setAdminCompaniesList([]);
        } else {
          toast.error(error.response.data?.detail || 'Failed to load companies list');
        }
      } else {
        toast.error('Failed to load companies list');
      }
      
      setAdminCompaniesList([]);
    } finally {
      setAdminCompaniesLoading(false);
    }
  };

  // SIMPLIFIED: Delete user account
  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    try {
      setIsDeleting(true);
      
      await axios.delete(
        CONFIG.getFullUrl(`/admin/users/${userToDelete.id}`),
        { headers: getAuthHeaders() }
      );
      
      // Remove user from state
      const updatedUsers = users.filter(user => user.id !== userToDelete.id);
      const updatedFilteredUsers = filteredUsers.filter(user => user.id !== userToDelete.id);
      
      setUsers(updatedUsers);
      setFilteredUsers(updatedFilteredUsers);
      
      toast.success(`User "${userToDelete.email}" deleted successfully!`);
      
      // Close modal and reset state
      setShowDeleteModal(false);
      setUserToDelete(null);
      
    } catch (error) {
      console.error('Error deleting user:', error);
      
      let errorMessage = 'Failed to delete user';
      if (error.response) {
        if (error.response.status === 403) {
          if (error.response.data && error.response.data.detail) {
            errorMessage = error.response.data.detail;
          } else {
            errorMessage = 'You do not have permission to delete this user';
          }
        } else if (error.response.status === 404) {
          errorMessage = 'User not found';
        } else if (error.response.status === 400) {
          if (error.response.data && error.response.data.detail) {
            errorMessage = error.response.data.detail;
          } else {
            errorMessage = 'Cannot delete this user';
          }
        } else if (error.response.data && error.response.data.detail) {
          errorMessage = error.response.data.detail;
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  // Open delete confirmation modal
  const handleOpenDeleteModal = (user) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  // Close delete confirmation modal
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  // Handle user selection for company access
  const handleUserSelect = async (user) => {
    try {
      setSelectedUser(user);
      
      // Get user's current companies
      const response = await axios.get(
        CONFIG.getFullUrl(CONFIG.API.USERS.USER_COMPANIES(user.id)),
        { headers: getAuthHeaders() }
      );
      
      // Set selected company IDs AND store the company objects
      const userCompaniesData = response.data || [];
      const userCompanyIds = userCompaniesData.map(company => company.id);
      
      setUserCompanies(userCompaniesData);
      setSelectedCompanyIds(userCompanyIds);
      
      setShowCompaniesModal(true);
      setShowDropdown(false);
    } catch (error) {
      console.error('Error fetching user companies:', error);
      
      if (error.response) {
        if (error.response.status === 404) {
          setUserCompanies([]);
          setSelectedCompanyIds([]);
          setShowCompaniesModal(true);
          setShowDropdown(false);
          toast.info('This user has no companies assigned yet.');
        } else {
          toast.error('Failed to load user companies');
        }
      } else {
        toast.error('Failed to load user companies');
      }
    }
  };

  // Handle company selection
  const handleCompanyToggle = (companyId) => {
    setSelectedCompanyIds(prev => {
      if (prev.includes(companyId)) {
        return prev.filter(id => id !== companyId);
      } else {
        return [...prev, companyId];
      }
    });
  };

  // Select all companies
  const handleSelectAll = () => {
    setSelectedCompanyIds(companies.map(company => company.id));
  };

  // Clear all selections
  const handleClearAll = () => {
    setSelectedCompanyIds([]);
  };

  // Save company access
  const handleSaveAccess = async () => {
    try {
      setIsSaving(true);
      
      await axios.post(
        CONFIG.getFullUrl(CONFIG.API.COMPANIES.MAP_TO_USER),
        {
          target_id: selectedUser.id,
          company_ids: selectedCompanyIds
        },
        { headers: getAuthHeaders() }
      );
      
      // Update user in the table
      const updatedUsers = users.map(u => {
        if (u.id === selectedUser.id) {
          const userCompanyObjects = companies.filter(c => selectedCompanyIds.includes(c.id));
          return {
            ...u,
            companies: userCompanyObjects,
            companyCount: selectedCompanyIds.length
          };
        }
        return u;
      });
      
      setUsers(updatedUsers);
      setFilteredUsers(updatedUsers);
      
      toast.success(`Company access updated successfully!`);
      setShowCompaniesModal(false);
      setSelectedUser(null);
      setSelectedCompanyIds([]);
      setUserCompanies([]);
      
    } catch (error) {
      console.error('Error updating company access:', error);
      
      if (error.response) {
        toast.error(error.response.data?.detail || 'Failed to update company access');
      } else {
        toast.error('Failed to update company access');
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Refresh data
  const handleRefresh = () => {
    fetchManagedUsers();
    fetchAdminCompanies();
  };

  // Open admin companies popup
  const handleOpenAdminCompaniesPopup = async () => {
    await fetchAdminCompaniesForPopup();
    setShowAdminCompaniesPopup(true);
  };

  // Close admin companies popup
  const handleCloseAdminCompaniesPopup = () => {
    setShowAdminCompaniesPopup(false);
  };

  // Handle sort
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort className="opacity-30" />;
    return sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />;
  };

  // Apply sorting
  const sortUsers = (usersList) => {
    const { key, direction } = sortConfig;
    
    return [...usersList].sort((a, b) => {
      let aValue = a[key];
      let bValue = b[key];
      
      // Handle different data types
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (aValue < bValue) {
        return direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
    
    if (totalPages <= maxVisiblePages) {
      // Show all pages if total pages is less than or equal to maxVisiblePages
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Always show first page
      pageNumbers.push(1);
      
      // Calculate start and end of visible pages
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);
      
      // Adjust if we're near the beginning
      if (currentPage <= 3) {
        endPage = 4;
      }
      
      // Adjust if we're near the end
      if (currentPage >= totalPages - 2) {
        startPage = totalPages - 3;
      }
      
      // Add ellipsis after first page if needed
      if (startPage > 2) {
        pageNumbers.push('...');
      }
      
      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
      
      // Add ellipsis before last page if needed
      if (endPage < totalPages - 1) {
        pageNumbers.push('...');
      }
      
      // Always show last page
      pageNumbers.push(totalPages);
    }
    
    return pageNumbers;
  };

  // Initial data fetch
  useEffect(() => {
    fetchManagedUsers();
    fetchAdminCompanies();
  }, []);

  // Filter users based on search term and apply sorting
  useEffect(() => {
    let result = users;
    
    // Apply search filter
    if (searchTerm.trim()) {
      result = users.filter(user =>
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply sorting
    result = sortUsers(result);
    
    setFilteredUsers(result);
    setCurrentPage(1);
  }, [searchTerm, users, sortConfig]);

  // Get username from email
  const getUsernameFromEmail = (email) => {
    return email ? email.split('@')[0] : 'User';
  };

  // Get selected companies
  const selectedCompanies = companies.filter(company => 
    selectedCompanyIds.includes(company.id)
  );

  // Stats calculation
  const stats = [
    { 
      label: 'Managed Users', 
      value: users.length, 
      icon: <FaUsers className="text-blue-500" />,
      color: 'bg-blue-50 dark:bg-blue-900/20' 
    },
    { 
      label: 'Companies', 
      value: companies.length, 
      icon: <FaBuilding className="text-green-500" />,
      color: 'bg-green-50 dark:bg-green-900/20' 
    },
    { 
      label: 'Total Access', 
      value: users.reduce((acc, user) => acc + (user.companyCount || 0), 0), 
      icon: <FaFilter className="text-purple-500" />,
      color: 'bg-purple-50 dark:bg-purple-900/20' 
    },
  ];

  // Pagination calculations
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);

  if (loading && users.length === 0) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading user access data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 relative h-full flex flex-col">
      {/* Header with Admin Companies Button */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">User Access Management</h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage company access for your users
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => (
          <div 
            key={index} 
            className="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 rounded-xl p-5 shadow-lg border border-gray-100 dark:border-gray-700 hover:shadow-xl transition-all duration-300"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">{stat.value}</p>
              </div>
              <div className={`p-3 rounded-full ${stat.color} shadow-inner`}>
                {typeof stat.icon === 'string' ? (
                  <span className="text-xl">{stat.icon}</span>
                ) : (
                  React.cloneElement(stat.icon, { className: "text-xl" })
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Search and Pagination Info */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search users by email address..."
            className="w-full pl-12 pr-4 py-3 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="text-sm text-gray-600 dark:text-gray-400 whitespace-nowrap">
          Showing {Math.min(indexOfFirstItem + 1, filteredUsers.length)} to {Math.min(indexOfLastItem, filteredUsers.length)} of {filteredUsers.length} users
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* Users Table Container */}
        <div className="flex-1 flex flex-col bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 rounded-xl shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden min-h-0">
          {filteredUsers.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center p-12">
              <div className="text-gray-400 dark:text-gray-500 mb-4">
                <FaUsers className="text-5xl mx-auto opacity-50" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {searchTerm ? 'No matching users found' : 'No users managed'}
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                {searchTerm 
                  ? 'Try adjusting your search terms' 
                  : 'Users will appear here once assigned'}
              </p>
              <button
                onClick={handleRefresh}
                className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg transition-all"
              >
                <FaSync /> Refresh Data
              </button>
            </div>
          ) : (
            <>
              {/* Table Header */}
              <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Managed Users ({filteredUsers.length})
                  </h3>
                  <div className="flex items-center gap-3">
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Page {currentPage} of {totalPages}
                    </div>
                    <button
                      onClick={handleRefresh}
                      className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                    >
                      <FaSync className={loading ? "animate-spin" : ""} />
                      Refresh
                    </button>
                  </div>
                </div>
              </div>

              {/* Scrollable Table Area */}
              <div className="flex-1 overflow-hidden">
                <div className="h-full overflow-y-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700/30 sticky top-0">
                      <tr>
                        <th 
                          scope="col" 
                          className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={() => handleSort('email')}
                        >
                          <div className="flex items-center gap-1">
                            User
                            {getSortIcon('email')}
                          </div>
                        </th>
                        <th 
                          scope="col" 
                          className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={() => handleSort('email')}
                        >
                          <div className="flex items-center gap-1">
                            Email
                            {getSortIcon('email')}
                          </div>
                        </th>
                        <th 
                          scope="col" 
                          className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={() => handleSort('is_active')}
                        >
                          <div className="flex items-center gap-1">
                            Status
                            {getSortIcon('is_active')}
                          </div>
                        </th>
                        <th 
                          scope="col" 
                          className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={() => handleSort('role')}
                        >
                          <div className="flex items-center gap-1">
                            Role
                            {getSortIcon('role')}
                          </div>
                        </th>
                        <th 
                          scope="col" 
                          className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                          onClick={() => handleSort('companyCount')}
                        >
                          <div className="flex items-center gap-1">
                            Access
                            {getSortIcon('companyCount')}
                          </div>
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-800">
                      {currentUsers.map((user) => (
                        <tr 
                          key={user.id} 
                          className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                        >
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-500 to-cyan-400 flex items-center justify-center text-white font-bold shadow-md">
                                {getUsernameFromEmail(user.email).charAt(0).toUpperCase()}
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900 dark:text-white">
                                  {getUsernameFromEmail(user.email)}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">
                                  ID: {user.id}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                            {user.email}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                              user.is_active
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                            }`}>
                              {user.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                              user.role === 'admin'
                                ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                            }`}>
                              {user.role === 'admin' ? 'Admin' : 'User'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="relative">
                                <div className="flex items-center">
                                  <span className="text-lg font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                                    {user.companyCount || 0}
                                  </span>
                                  <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                                    company{user.companyCount !== 1 ? 's' : ''}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleUserSelect(user)}
                                className="inline-flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg transition-all shadow-md hover:shadow-lg text-sm"
                                title="Manage company access"
                              >
                                <FaEye className="text-xs" />
                                Manage
                              </button>
                              
                              {/* DELETE USER BUTTON */}
                              <button
                                onClick={() => handleOpenDeleteModal(user)}
                                className="inline-flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-red-500 to-pink-600 hover:from-red-600 hover:to-pink-700 text-white rounded-lg transition-all shadow-md hover:shadow-lg text-sm"
                                title="Delete user account"
                              >
                                <FaTrash className="text-xs" />
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination - Fixed at bottom of container */}
              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/30">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredUsers.length)} of {filteredUsers.length} users
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                      >
                        <FaChevronLeft className="text-xs" />
                        Previous
                      </button>
                      
                      <div className="flex items-center gap-1">
                        {getPageNumbers().map((pageNum, index) => (
                          pageNum === '...' ? (
                            <span key={`ellipsis-${index}`} className="px-2 text-gray-400">
                              ...
                            </span>
                          ) : (
                            <button
                              key={pageNum}
                              onClick={() => setCurrentPage(pageNum)}
                              className={`w-8 h-8 flex items-center justify-center text-sm rounded-lg transition-colors ${
                                currentPage === pageNum
                                  ? 'bg-blue-600 text-white shadow-sm'
                                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                              }`}
                            >
                              {pageNum}
                            </button>
                          )
                        ))}
                      </div>
                      
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                      >
                        Next
                        <FaChevronRight className="text-xs" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* SIMPLE SMALL DELETE CONFIRMATION MODAL */}
      {showDeleteModal && userToDelete && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[100] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-sm">
            {/* Modal Header */}
            <div className="px-5 py-4 border-b dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 dark:bg-red-900/20 rounded-lg">
                  <FaExclamationTriangle className="text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Delete User Account
                </h3>
              </div>
            </div>

            {/* Modal Content - Simple */}
            <div className="p-5">
              <div className="text-center mb-4">
                <div className="flex justify-center mb-3">
                  <div className="h-16 w-16 rounded-full bg-gradient-to-r from-red-500 to-pink-600 flex items-center justify-center text-white font-bold text-xl">
                    {getUsernameFromEmail(userToDelete.email).charAt(0).toUpperCase()}
                  </div>
                </div>
                <p className="text-gray-700 dark:text-gray-300 font-medium mb-1">
                  {getUsernameFromEmail(userToDelete.email)}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                  {userToDelete.email}
                </p>
                <p className="text-gray-600 dark:text-gray-400">
                  Are you sure you want to delete this user account?
                </p>
                <p className="text-sm text-red-500 dark:text-red-400 mt-2">
                  This action cannot be undone.
                </p>
              </div>
            </div>

            {/* Modal Footer - Simple */}
            <div className="px-5 py-4 border-t dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50 rounded-b-xl">
              <div className="flex justify-end gap-3">
                <button
                  onClick={handleCloseDeleteModal}
                  disabled={isDeleting}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteUser}
                  disabled={isDeleting}
                  className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-red-600 to-pink-700 hover:from-red-700 hover:to-pink-800 rounded-lg transition-colors shadow-md hover:shadow-lg disabled:opacity-50 flex items-center gap-2"
                >
                  {isDeleting ? (
                    <>
                      <FaSync className="animate-spin text-sm" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <FaTrash className="text-sm" />
                      Confirm Delete
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admin Companies List Popup */}
      {showAdminCompaniesPopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col">
            {/* Popup Header */}
            <div className="px-5 py-4 border-b dark:border-gray-700 flex items-center justify-between bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-700 dark:to-gray-800 rounded-t-xl">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg">
                  <FaList className="text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                    Admin Companies List
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    Companies you manage ({adminCompaniesList.length})
                  </p>
                </div>
              </div>
              <button
                onClick={handleCloseAdminCompaniesPopup}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                title="Close"
              >
                <FaTimesCircle className="text-lg" />
              </button>
            </div>

            {/* Popup Content */}
            <div className="flex-1 overflow-y-auto p-5">
              {adminCompaniesLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-3 text-gray-600 dark:text-gray-400">Loading companies...</p>
                  </div>
                </div>
              ) : adminCompaniesList.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-gray-400 dark:text-gray-600 mb-4">
                    <FaBuilding className="text-4xl mx-auto opacity-50" />
                  </div>
                  <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    No Companies Managed
                  </h4>
                  <p className="text-gray-600 dark:text-gray-400">
                    You don't have any companies assigned to manage.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {adminCompaniesList.map((company) => (
                    <div 
                      key={company.id}
                      className="group p-4 bg-gradient-to-r from-gray-50 to-white dark:from-gray-700/50 dark:to-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-500 hover:shadow-md transition-all duration-200"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                              {company.name}
                            </span>
                            {company.is_managed_by_current_user && (
                              <span className="text-xs bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300 px-2 py-0.5 rounded-full">
                                Managed
                              </span>
                            )}
                          </div>
                          
                          {company.ticker_id && (
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-600 dark:text-gray-400">
                                Ticker: 
                              </span>
                              <span className="text-sm font-medium bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                                {company.ticker_id}
                              </span>
                            </div>
                          )}
                          
                          <div className="mt-2 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                            <span className="inline-flex items-center gap-1">
                              <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                              ID: {company.id}
                            </span>
                          </div>
                        </div>
                        
                        <div className="ml-4 flex flex-col items-end">
                          <span className="text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 px-2 py-1 rounded">
                            Available
                          </span>
                          <button
                            onClick={() => {
                              toast.info(`Company: ${company.name} selected`);
                            }}
                            className="mt-2 p-1.5 text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                            title="Select Company"
                          >
                            <FaExternalLinkAlt size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Popup Footer */}
            <div className="px-5 py-4 border-t dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50 rounded-b-xl">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Showing <span className="font-semibold">{adminCompaniesList.length}</span> companies
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleRefresh}
                    className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors flex items-center gap-1"
                  >
                    <FaSync className={adminCompaniesLoading ? "animate-spin" : ""} />
                    Refresh
                  </button>
                  <button
                    onClick={handleCloseAdminCompaniesPopup}
                    className="px-3 py-1.5 text-sm bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Compact Professional Modal with Collapsible Sections */}
      {showCompaniesModal && selectedUser && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg">
            {/* Modal Header - More Compact */}
            <div className="px-5 py-3 border-b dark:border-gray-700 flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                  Manage Company Access
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs">
                  {selectedUser.email}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowCompaniesModal(false);
                  setSelectedUser(null);
                  setSelectedCompanyIds([]);
                  setUserCompanies([]);
                }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                ✕
              </button>
            </div>

            {/* Modal Content - More Compact */}
            <div className="p-5">
              {/* Current Access Summary - Always Visible */}
              <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="h-8 w-8 rounded-full bg-gradient-to-r from-green-500 to-emerald-400 flex items-center justify-center text-white text-sm font-bold">
                      {userCompanies.length}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        Currently has access to {userCompanies.length} companies
                      </p>
                      {userCompanies.length > 0 && (
                        <button
                          onClick={() => setShowDropdown(!showDropdown)}
                          className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center gap-1 mt-1"
                        >
                          {showDropdown ? 'Hide list' : 'View list'}
                          {showDropdown ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
                        </button>
                      )}
                    </div>
                  </div>
                  {userCompanies.length > 0 && (
                    <span className="text-xs bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 px-2 py-1 rounded-full">
                      ✓ Active
                    </span>
                  )}
                </div>
                
                {/* Collapsible Current Companies List */}
                {showDropdown && userCompanies.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                    <div className="text-xs text-gray-600 dark:text-gray-400 mb-2">Current companies:</div>
                    <div className="space-y-2 max-h-32 overflow-y-auto pr-2">
                      {userCompanies.map((company) => (
                        <div 
                          key={company.id}
                          className="flex items-center justify-between text-sm p-2 hover:bg-gray-100 dark:hover:bg-gray-600/50 rounded"
                        >
                          <div className="flex items-center gap-2">
                            <div className="h-5 w-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                              <FaCheck className="text-green-600 dark:text-green-400" size={10} />
                            </div>
                            <span className="text-gray-900 dark:text-white">{company.name}</span>
                          </div>
                          {company.ticker_id && (
                            <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                              {company.ticker_id}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Quick Selection Controls */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                    Select Companies to Grant Access
                  </h4>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Choose from {companies.length} available companies
                  </p>
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={handleSelectAll}
                    className="text-xs px-2 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded border border-blue-200 dark:border-blue-800"
                  >
                    All
                  </button>
                  <button
                    onClick={handleClearAll}
                    className="text-xs px-2 py-1 bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-600 rounded border border-gray-200 dark:border-gray-600"
                  >
                    Clear
                  </button>
                </div>
              </div>

              {/* Compact Companies Selection Grid */}
              <div className="border dark:border-gray-700 rounded-lg overflow-hidden">
                <div className="max-h-64 overflow-y-auto">
                  {companies.length === 0 ? (
                    <div className="p-8 text-center">
                      <FaBuilding className="text-gray-300 dark:text-gray-600 text-xl mx-auto mb-2" />
                      <p className="text-gray-500 dark:text-gray-400 text-sm">No companies available</p>
                    </div>
                  ) : (
                    <div className="divide-y dark:divide-gray-700">
                      {companies.map((company) => {
                        const isSelected = selectedCompanyIds.includes(company.id);
                        const isCurrentlyAssigned = userCompanies.some(c => c.id === company.id);
                        
                        return (
                          <label
                            key={company.id}
                            className={`flex items-center gap-3 p-3 cursor-pointer transition-all ${
                              isSelected 
                                ? 'bg-blue-50 dark:bg-blue-900/20' 
                                : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                            } ${isCurrentlyAssigned ? 'border-l-2 border-green-500' : ''}`}
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleCompanyToggle(company.id)}
                                className="h-4 w-4 text-blue-600 rounded border-gray-300 dark:border-gray-600 focus:ring-blue-500 flex-shrink-0"
                              />
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                    {company.name}
                                  </span>
                                  {isCurrentlyAssigned && (
                                    <span className="text-xs text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30 px-1.5 py-0.5 rounded flex-shrink-0">
                                      Has access
                                    </span>
                                  )}
                                </div>
                                {company.ticker_id && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                    {company.ticker_id}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className={`h-2 w-2 rounded-full flex-shrink-0 ${
                              isSelected ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
                            }`} />
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Selected Count Preview - Very Compact */}
              {selectedCompanies.length > 0 && (
                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {selectedCompanies.length} selected
                    </span>
                    <span className="text-xs text-blue-600 dark:text-blue-400">
                      {selectedCompanies.length - userCompanies.length} new
                    </span>
                  </div>
                  {/* Compact chips for selected companies */}
                  <div className="mt-2 flex flex-wrap gap-1">
                    {selectedCompanies.slice(0, 3).map(company => {
                      const isCurrentlyAssigned = userCompanies.some(c => c.id === company.id);
                      return (
                        <div
                          key={company.id}
                          className={`text-xs px-2 py-1 rounded-full ${
                            isCurrentlyAssigned
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                              : 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300'
                          }`}
                        >
                          {company.name.substring(0, 10)}{company.name.length > 10 ? '...' : ''}
                          {isCurrentlyAssigned && ' ✓'}
                        </div>
                      );
                    })}
                    {selectedCompanies.length > 3 && (
                      <div className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full">
                        +{selectedCompanies.length - 3} more
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer - Compact */}
            <div className="px-5 py-3 border-t dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50 rounded-b-xl">
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {selectedCompanies.length} of {companies.length} selected
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setShowCompaniesModal(false);
                      setSelectedUser(null);
                      setSelectedCompanyIds([]);
                      setUserCompanies([]);
                    }}
                    className="px-3 py-1.5 text-xs text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveAccess}
                    disabled={isSaving}
                    className="px-3 py-1.5 text-xs bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-lg disabled:opacity-50 flex items-center gap-1"
                  >
                    {isSaving ? (
                      <>
                        <FaSync className="animate-spin" size={12} />
                        Saving...
                      </>
                    ) : (
                      <>
                        <FaCheck size={12} />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserAccessContent;